// reportWording.js — the sentences of the launch sign-off report, exactly as
// docs/signoff-report-template.md fixes them. buildLaunchReport.js renders; this
// module decides the words. Every function is pure, takes run results, and never
// throws (a bad input degrades to the plainest true sentence, never to prose).
//
// Change the TEMPLATE first, then this file. Its wording rules apply here:
// numbers not adjectives, no hedges, absence is not failure, count passes with
// `satisfied` (never `found`), times in UTC.

import { describeScope } from './describeCheck';
import { describeSource } from './evidenceLine';
import { platformOfRow } from './launchScope';
import { excludedRowsCopy, MIXED_OWNERS_COPY } from './isolationCopy';

const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

/** Cut at a word boundary with an ellipsis — never mid-word. */
export function shortWords(text, max = 48) {
  const t = String(text || '').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > max * 0.5 ? cut.slice(0, sp) : cut).replace(/[\s,;:.-]+$/, '')}…`;
}

/** Short name of a check for a sentence: displayName, else its id. */
export function checkName(def, checkId) {
  const n = def && (def.displayName || def.title || def.name);
  return n ? String(n) : String(checkId || 'check');
}

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const failedCount = (r) => (Array.isArray(r?.failures) ? r.failures.length : (num(r?.failed) ?? 0));

/**
 * reasonFor(criterion, result, def) -> { code, text } | null
 * The closed list of reasons a criterion or check is Needs review / Not run
 * (template §3). Null when the result is a plain pass or fail.
 */
export function reasonFor(criterion, result, def) {
  try {
    const ids = Array.isArray(criterion?.checkIds) ? criterion.checkIds.filter(Boolean) : [];
    if (criterion && (criterion.mapping === 'unmapped' || (!ids.length && criterion.mapping !== 'manual'))) {
      return { code: 'unmapped', text: 'no check is mapped to this criterion' };
    }
    if (!result) return { code: 'not_in_run', text: 'its checks were not part of this run' };
    const st = result.status;
    if (st === 'pass' || st === 'fail') return null;
    const matched = num(result.matched) ?? 0;
    if (matched === 0) {
      let scope = 'this check';
      try { scope = describeScope(def) || scope; } catch { /* keep */ }
      return { code: 'no_traffic', text: `no request in scope matched ${scope.replace(/\.$/, '')}` };
    }
    const rows = Array.isArray(result.rows) ? result.rows : [];
    const unreadable = rows.filter((r) => r && r.unreadable).length;
    if (unreadable && !(num(result.satisfied) > 0)) {
      const usb = rows.some((r) => r && r.unreadableKind === 'usb');
      const src = describeSource(def?.assert?.source, null);
      return { code: 'unreadable', text: `the ${src} was unreadable on ${unreadable} ${plural(unreadable, 'request')} (${usb ? 'USB capture has no responses' : 'body not kept'})` };
    }
    if (def?.severity === 'info' || st === 'info') {
      return { code: 'info_absence', text: 'the check is informational and the event was not observed (absence is not a failure)' };
    }
    if (num(result.found) === 0) {
      const a = def?.assert || {};
      return { code: 'value_absent', text: `${describeSource(a.source, a.param ?? a.path ?? a.key)} was absent on all ${matched} matching ${plural(matched, 'request')}` };
    }
    return { code: 'value_absent', text: `the check warned on ${failedCount(result)} of ${matched} ${plural(matched, 'request')}` };
  } catch {
    return { code: 'not_in_run', text: 'its checks were not part of this run' };
  }
}

/** One sentence for one check result (template §3 "Verdict sentence"). */
export function checkSentence(def, checkId, result, criterion) {
  const name = checkName(def, checkId);
  const looked = num(result?.matched) ?? 0;
  if (result?.status === 'pass') {
    const sat = num(result.satisfied) ?? looked;
    return `${name} passed on ${sat} of ${looked} ${plural(looked, 'request')}.`;
  }
  if (result?.status === 'fail') {
    return `${name} failed on ${failedCount(result)} of ${looked} ${plural(looked, 'request')}.`;
  }
  const r = reasonFor(criterion, result, def);
  return `${name}: ${r ? r.text : 'not confirmed'}.`;
}

/**
 * criterionSentence(status, criterion, firstResult, firstDef) -> string
 * The bold verdict sentence at the top of a criterion card.
 */
export function criterionSentence(status, criterion, firstResult, firstDef) {
  switch (status) {
    case 'met': return 'Met.';
    case 'not-met': return 'Not met.';
    case 'manual-review': {
      const f = floorDowngrade(criterion);
      return f
        ? `Manual review. Its checks were not counted as proof: the compiler's confidence was ${f.pct}%, below the ${f.floor}% floor.`
        : 'Manual review. Network traffic cannot prove this.';
    }
    case 'needs-review': {
      const r = reasonFor(criterion, firstResult, firstDef);
      return `Needs review. No captured request proves or disproves this: ${r ? r.text : 'the checks did not settle it'}.`;
    }
    default: {
      const r = reasonFor(criterion, firstResult, firstDef);
      return `Not run. ${r ? r.text.charAt(0).toUpperCase() + r.text.slice(1) : 'No check result exists'}.`;
    }
  }
}

/**
 * floorDowngrade(criterion) -> { pct, floor } | null
 * A criterion the compiler sent to manual because its confidence fell under the
 * 50% floor (compileCriteria.js). Traffic may well prove it; the mapping did not.
 */
export function floorDowngrade(criterion) {
  const why = String((criterion && criterion.rationale) || '');
  if (!/downgraded to manual: confidence below floor/.test(why)) return null;
  const c = Number(criterion.confidence);
  return { pct: Number.isFinite(c) ? Math.round(c * 100) : '?', floor: 50 };
}

/** "To confirm: …" for anything unproven (template §3). */
export function toConfirmLine(def, reasonCode = null, reasonText = '') {
  if (reasonCode === 'unreadable') {
    return /USB/.test(reasonText)
      ? 'To confirm: capture the same flow over the Wi-Fi proxy (USB capture has no response bodies), then re-run.'
      : 'To confirm: re-run Batch from the saved HAR of this session (it keeps response bodies), then regenerate this report.';
  }
  if (reasonCode === 'unmapped') return 'To confirm: map a check to this criterion (re-compile, or attach one), then re-run.';
  if (reasonCode === 'not_in_run') return 'To confirm: re-run the checks on a capture and regenerate this report.';
  if (!def) return 'To confirm: map a check to this criterion (re-compile, or attach one), then re-run.';
  const fix = def && typeof def.fix === 'string' && def.fix.trim();
  if (fix) return `To confirm: ${fix.replace(/\.$/, '')}.`;
  let scope = '';
  try { scope = describeScope(def) || ''; } catch { /* keep */ }
  return scope ? `To confirm: capture a session where ${scope.replace(/\.$/, '').replace(/^Grades /, '')} occurs.` : 'To confirm: capture a session where this traffic occurs.';
}

const utc = (v) => {
  const t = v == null ? NaN : (typeof v === 'number' ? v : Date.parse(v));
  return Number.isFinite(t) ? new Date(t).toISOString().slice(11, 19) : null;
};
const rowTime = (r) => r && (r.at ?? r.ts ?? r.timestamp ?? r.startedAtISO ?? r.startedAt);
const hostOf = (u) => { try { return new URL(u).host; } catch { return null; } };

/** "Scope: N requests on hosts between a–b UTC (platform, capture)." */
export function scopeLine(results, { platform, captureLabel } = {}) {
  const rows = (results || []).flatMap((r) => (Array.isArray(r?.rows) ? r.rows : [])).filter(Boolean);
  const looked = (results || []).reduce((n, r) => n + (num(r?.matched) ?? 0), 0);
  if (!looked) return null;
  const counts = new Map();
  for (const r of rows) { const h = hostOf(r.url); if (h) counts.set(h, (counts.get(h) || 0) + 1); }
  const hosts = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([h]) => h);
  const times = rows.map((r) => Date.parse(rowTime(r))).filter(Number.isFinite).sort((a, b) => a - b);
  const window = times.length ? ` between ${utc(times[0])}–${utc(times[times.length - 1])} UTC` : '';
  const ctx = [platform, captureLabel].filter(Boolean).join(', ');
  return `Scope: ${looked} ${plural(looked, 'request')}${hosts.length ? ` on ${hosts.join(', ')}` : ''}${window}${ctx ? ` (${ctx})` : ''}.`;
}

/**
 * overallVerdict(statuses, { coverageIncomplete }) -> { headline, line, tone }
 * Template §2: exactly one line, first match wins. `statuses` is
 * [{ n, text, status }] in criterion order.
 */
export function overallVerdict(statuses, { coverageIncomplete = false } = {}) {
  const list = Array.isArray(statuses) ? statuses : [];
  const N = list.length;
  const fmt = (xs) => xs.map((x) => `${x.n} (${shortWords(x.text, 48)})`).join(', ');
  const notMet = list.filter((x) => x.status === 'not-met');
  const open = list.filter((x) => x.status === 'needs-review' || x.status === 'not-run');
  const manual = list.filter((x) => x.status === 'manual-review');
  if (!N) return { headline: 'NOT READY', tone: 'warn', line: 'No criteria have been compiled for this launch.' };
  if (notMet.length) return { headline: 'NOT READY', tone: 'fail', line: `${notMet.length} of ${N} criteria are not met: ${fmt(notMet)}.` };
  if (open.length || coverageIncomplete) {
    const xs = open.length ? open : list;
    return { headline: 'NOT READY', tone: 'warn', line: `${xs.length} of ${N} criteria could not be confirmed from traffic: ${fmt(xs)}.` };
  }
  if (manual.length) {
    const t = N - manual.length;
    return { headline: 'READY PENDING MANUAL CHECKS', tone: 'info', line: `All ${t} traffic-provable criteria are met; ${manual.length} need a tester's confirmation: ${fmt(manual)}.` };
  }
  return { headline: 'READY TO SIGN', tone: 'pass', line: `All ${N} criteria are met by captured traffic.` };
}

/**
 * limitsOfCapture(rows) -> string[]  (template §5, verbatim lines, only those that apply)
 * `rows` are the full captured rows the run graded (LaunchPage's scoped rows).
 */
export function limitsOfCapture(rows, { scope = null } = {}) {
  const out = [];
  // Per-user isolation (template §5, AC 123): the proxy's `scope` on the rollup/run.
  // Printed even with zero graded rows, because "everything was excluded" is exactly when it matters.
  const excluded = excludedRowsCopy(scope && scope.excludedUnownedOwnRows);
  const mixed = scope && scope.mixedOwners === true ? MIXED_OWNERS_COPY : '';
  const tail = () => { if (mixed) out.push(mixed); if (excluded) out.push(excluded); return out; };
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (!list.length) return tail();
  const q = (r) => { try { return new URL(r.url).searchParams; } catch { return null; } };
  const gam = list.filter((r) => /\/gampad\/ads/.test(r.url || ''));
  if (list.some((r) => r.captureSource === 'logcat')) {
    out.push('USB capture reads request URLs from the device log: no responses, no Prebid/APS traffic, URLs cut at ~4 KB.');
  }
  const adtest = gam.filter((r) => q(r)?.get('adtest') === 'on').length;
  if (adtest) out.push(`Auctions ran in test mode on ${adtest} of ${gam.length} GAM requests: bids and creatives are test values, not live demand.`);
  // A buyer SDK in ITS OWN test mode: its decoded logs carry test=true tracking
  // calls. Independent of adtest — on 19 Oct Liftoff kept its fixed 47.50 test bid
  // and test creatives with adtest off, so "live auction" did not mean live demand.
  const testBuyers = [...new Set(list
    .filter((r) => (r.requestType || r.type) === 'sdk_bidding' && r.validData && Array.isArray(r.validData.messages)
      && r.validData.messages.some((m) => /[?&]test=true\b/.test(String(m))))
    .map((r) => String(r.validData.networkName || 'A buyer SDK').replace(/\s*\(.*\)$/, '')))];
  for (const b of testBuyers) {
    out.push(`${b} answered in its own test mode (its tracking calls carry test=true): its wins are test creatives at a fixed bid, not live demand${adtest === 0 && gam.length ? ', even though adtest was off' : ''}.`);
  }
  const optOut = [...new Set(gam.map((r) => q(r)?.get('us_privacy')).filter((v) => v && /^1.[Yy]/.test(v)))];
  if (optOut.length) out.push(`The user had opted out of sale (us_privacy=${optOut.join(', ')}): demand is restricted.`);
  const stripped = list.filter((r) => r._bodyStripped || r.bodyStripped).length;
  if (stripped) out.push(`${stripped} of ${list.length} requests reached this run without their response body (the browser keeps bodies for the newest 300 only); checks that read response bodies could not grade those requests.`);
  const chaosHosts = [...new Set(list.filter((r) => r.chaos && (r.chaos.action === 'block' || r.chaos.action === 'mock')).map((r) => hostOf(r.url)).filter(Boolean))];
  if (chaosHosts.length) out.push(`Requests to ${chaosHosts.slice(0, 4).join(', ')} were blocked or mocked by a Chaos rule and do not reflect the vendor.`);
  const rewrites = [...new Set(list.flatMap((r) => (r.iuRewrite && Array.isArray(r.iuRewrite.changes) ? r.iuRewrite.changes : []))
    .map((c) => c && (c.label || c.field)).filter(Boolean))];
  if (rewrites.length) out.push(`Requests were rewritten before leaving the proxy: ${rewrites.slice(0, 4).join('; ')}.`);
  return tail();
}

/**
 * observations(rows) -> [{ severity, what, count, at }]  (template §4)
 * Warn/fail findings on the captured rows, grouped by code, plus buyer beacons
 * that fired twice for one auction. They never change a verdict.
 */
export function observations(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const groups = new Map();
  for (const r of list) {
    for (const i of Array.isArray(r.issues) ? r.issues : []) {
      const sev = i && (i.level || i.severity);
      if (sev !== 'warn' && sev !== 'fail') continue;
      const code = i.code || (i.msg || i.message || '').slice(0, 40);
      if (!code) continue;
      const raw = String(i.msg || i.message || i.note || code).split(' — ')[0];
      // A finding that quotes a URL names its host + path, never a query cut mid-way.
      const what = raw.replace(/https?:\/\/([^\s/?#]+)([^\s?#]*)[^\s]*/g, '$1$2').slice(0, 110);
      const g = groups.get(code) || { severity: sev, what, count: 0, at: null };
      g.count += 1;
      const t = Date.parse(rowTime(r));
      if (Number.isFinite(t) && (g.at == null || t < g.at)) g.at = t;
      groups.set(code, g);
    }
  }
  // Same buyer beacon URL fired more than once (e.g. Liftoff impression refire).
  const beacons = new Map();
  for (const r of list) {
    if (!/liftoff\.io\/vungle\/beacon|\/api\/bill\b/.test(r.url || '')) continue;
    const b = beacons.get(r.url) || { n: 0, at: null };
    b.n += 1;
    const t = Date.parse(rowTime(r));
    if (Number.isFinite(t) && b.at == null) b.at = t;
    beacons.set(r.url, b);
  }
  const repeats = [...beacons.values()].filter((b) => b.n > 1);
  const out = [...groups.values()].sort((a, b) => b.count - a.count).slice(0, 8);
  if (repeats.length) {
    out.unshift({ severity: 'warn', what: 'Buyer impression beacon refired for the same auction', count: repeats.length, at: Math.min(...repeats.map((b) => b.at).filter(Number.isFinite)) });
  }
  return out.map((o) => ({ ...o, at: Number.isFinite(o.at) ? `${utc(o.at)} UTC` : null }));
}

/**
 * captureFacts(rows) -> { window, requests, testMode } for the report header
 * (template §1). Strings; '' when unknown so the header can print its empty value.
 */
export function captureFacts(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const times = list.map((r) => Date.parse(rowTime(r))).filter(Number.isFinite).sort((a, b) => a - b);
  const day = (t) => new Date(t).toISOString().slice(0, 10);
  const window = times.length
    ? `${day(times[0])} ${utc(times[0])}–${day(times[times.length - 1]) === day(times[0]) ? '' : `${day(times[times.length - 1])} `}${utc(times[times.length - 1])} UTC`
    : '';
  const gam = list.filter((r) => /\/gampad\/ads/.test(r.url || ''));
  const on = gam.filter((r) => { try { return new URL(r.url).searchParams.get('adtest') === 'on'; } catch { return false; } }).length;
  const seen = [...new Set(list.map(platformOfRow).filter((p) => p && p !== 'Other'))].sort();
  return {
    window,
    requests: list.length ? String(list.length) : '',
    testMode: gam.length ? `adtest=on on ${on} of ${gam.length} GAM requests` : '',
    platform: seen.length ? `${seen.join(' + ')} (seen in traffic; not scoped)` : '',
  };
}

/**
 * mixedNeedsReview(checks, criterion, defOf) -> string | null
 * "Needs review" on a criterion where SOME checks passed: say which passed and
 * which could not be confirmed (and why), instead of the blanket "no captured
 * request proves or disproves this" (wrong when a check passed on real requests).
 */
export function mixedNeedsReview(checks, criterion, defOf) {
  const list = Array.isArray(checks) ? checks : [];
  const passed = list.filter(({ result }) => result && result.status === 'pass');
  const open = list.filter(({ result }) => !result || (result.status !== 'pass' && result.status !== 'fail'));
  if (!passed.length || !open.length) return null;
  const opens = open.map(({ checkId, result }) => {
    const def = defOf(checkId);
    const r = reasonFor(criterion, result ?? null, def);
    return `${checkName(def, checkId)} (${r ? r.text : 'not confirmed'})`;
  });
  return `Needs review. ${passed.length} of ${list.length} checks passed; ${open.length} could not be confirmed: ${opens.join('; ')}.`;
}
